import { useState, useRef, useCallback, useEffect } from "react";
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import VinScanner from "./VinScanner";

// Cloud Function base URL — update this after first deploy
const FUNCTIONS_BASE = "https://us-central1-laneiq-ae0cd.cloudfunctions.net";

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["DC","Washington D.C."]
];

const CATEGORIES = [
  { id:"oil",          label:"Oil Change",      icon:"🛢️" },
  { id:"brakes",       label:"Brakes",          icon:"🔴" },
  { id:"tires",        label:"Tires",           icon:"⚫" },
  { id:"battery",      label:"Battery",         icon:"⚡" },
  { id:"filters",      label:"Filters",         icon:"🌬️" },
  { id:"wipers",       label:"Wipers",          icon:"💧" },
  { id:"coolant",      label:"Coolant",         icon:"🌡️" },
  { id:"transmission", label:"Transmission",    icon:"⚙️" },
];

const TIER_CONFIG = {
  best:   { label:"Best",   sub:"OEM / Dealer",       color:"#F5A623", dim:"rgba(245,166,35,0.10)", border:"rgba(245,166,35,0.35)" },
  better: { label:"Better", sub:"Premium Aftermarket", color:"#3B82F6", dim:"rgba(59,130,246,0.10)", border:"rgba(59,130,246,0.35)" },
  good:   { label:"Good",   sub:"Value Aftermarket",   color:"#22C55E", dim:"rgba(34,197,94,0.10)",  border:"rgba(34,197,94,0.35)" },
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0E1117; --surface: #161B27; --surface2: #1C2333; --surface3: #212A3A;
    --border: #252D3D; --accent: #F5A623; --accent-dim: rgba(245,166,35,0.10);
    --blue: #3B82F6; --blue-dim: rgba(59,130,246,0.10);
    --green: #22C55E; --green-dim: rgba(34,197,94,0.10);
    --red: #EF4444; --red-dim: rgba(239,68,68,0.12);
    --orange: #F97316; --orange-dim: rgba(249,115,22,0.12);
    --text: #F1F5F9; --text-sub: #94A3B8; --text-muted: #475569;
  }
  body { background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; min-height: 100vh; }
  .hdr { display:flex; align-items:center; justify-content:space-between; padding:0 28px; height:60px; background:rgba(22,27,39,0.97); backdrop-filter:blur(12px); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:100; }
  .hdr-logo { display:flex; align-items:center; gap:10px; }
  .hdr-logo-icon { width:30px; height:30px; background:var(--accent); border-radius:6px; display:flex; align-items:center; justify-content:center; font-family:'Bebas Neue',sans-serif; font-size:17px; color:#000; }
  .hdr-logo-text { font-family:'Bebas Neue',sans-serif; font-size:22px; letter-spacing:3px; }
  .hdr-logo-text span { color:var(--accent); }
  .hdr-badge { background:var(--green-dim); border:1px solid rgba(34,197,94,0.3); border-radius:20px; padding:3px 12px; font-size:11px; font-weight:700; color:var(--green); letter-spacing:1px; text-transform:uppercase; }
  .hdr-right { display:flex; align-items:center; gap:12px; }
  .hdr-user { font-size:12px; color:var(--text-muted); }
  .hdr-user strong { color:var(--text-sub); }
  .hdr-btn { background:transparent; border:1px solid var(--border); border-radius:6px; padding:5px 12px; font-size:12px; color:var(--text-muted); cursor:pointer; font-family:'DM Sans',sans-serif; transition:all 0.15s; }
  .hdr-btn:hover { border-color:var(--text-sub); color:var(--text); }
  .search-zone { background:var(--surface); border-bottom:1px solid var(--border); padding:20px 28px; }
  .search-row { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
  .search-group { display:flex; flex-direction:column; gap:5px; }
  .search-label { font-size:10px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:var(--text-muted); }
  .search-input { background:var(--surface2); border:1.5px solid var(--border); border-radius:8px; padding:10px 14px; font-size:14px; color:var(--text); font-family:'JetBrains Mono',monospace; outline:none; transition:border-color 0.15s; width:220px; }
  .search-input:focus { border-color:var(--blue); }
  .search-input::placeholder { color:var(--text-muted); font-family:'DM Sans',sans-serif; font-size:12px; }
  .or-divider { display:flex; align-items:center; padding-bottom:10px; color:var(--text-muted); font-size:11px; font-weight:700; letter-spacing:1px; }
  .plate-row { display:flex; gap:8px; align-items:center; }
  .state-select { background:var(--surface2); border:1.5px solid var(--border); border-radius:8px; padding:10px 12px; font-size:13px; color:var(--text); font-family:'DM Sans',sans-serif; outline:none; transition:border-color 0.15s; width:165px; cursor:pointer; }
  .state-select:focus { border-color:var(--blue); }
  .search-btn { background:var(--accent); color:#000; border:none; border-radius:8px; padding:10px 22px; font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; transition:opacity 0.15s; white-space:nowrap; align-self:flex-end; height:42px; }
  .search-btn:hover { opacity:0.88; }
  .search-btn:disabled { opacity:0.45; cursor:not-allowed; }
  .clear-btn { background:transparent; border:1.5px solid var(--border); border-radius:8px; padding:10px 14px; font-size:12px; color:var(--text-muted); cursor:pointer; font-family:'DM Sans',sans-serif; transition:all 0.15s; height:42px; align-self:flex-end; }
  .clear-btn:hover { border-color:var(--red); color:var(--red); }
  .state-default-note { font-size:11px; color:var(--text-muted); margin-top:4px; }
  .state-default-note em { color:var(--accent); font-style:normal; }
  .mileage-bar { background:var(--surface2); border-bottom:1px solid var(--border); padding:12px 28px; display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
  .mileage-label { font-size:12px; font-weight:700; color:var(--text-sub); white-space:nowrap; }
  .mileage-input { background:var(--surface); border:1.5px solid var(--border); border-radius:7px; padding:8px 12px; font-size:14px; color:var(--text); font-family:'JetBrains Mono',monospace; outline:none; width:170px; transition:border-color 0.15s; }
  .mileage-input:focus { border-color:var(--accent); }
  .mileage-input::placeholder { color:var(--text-muted); font-family:'DM Sans',sans-serif; font-size:12px; }
  .mileage-save-btn { background:var(--accent-dim); border:1px solid rgba(245,166,35,0.3); border-radius:7px; padding:7px 14px; font-size:12px; font-weight:700; color:var(--accent); cursor:pointer; font-family:'DM Sans',sans-serif; }
  .mileage-save-btn:hover { background:rgba(245,166,35,0.2); }
  .mileage-history { font-size:11px; color:var(--text-muted); }
  .mileage-history strong { color:var(--text-sub); }
  .mileage-required { font-size:11px; color:var(--red); font-weight:600; }
  .empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 40px; gap:16px; text-align:center; }
  .empty-icon { font-size:56px; opacity:0.4; }
  .empty-title { font-family:'Bebas Neue',sans-serif; font-size:28px; letter-spacing:2px; color:var(--text-sub); }
  .empty-sub { font-size:14px; color:var(--text-muted); max-width:380px; line-height:1.6; }
  .loading-wrap { display:flex; flex-direction:column; align-items:center; padding:80px; gap:14px; }
  .loading-spinner { width:44px; height:44px; border:3px solid var(--border); border-top-color:var(--accent); border-radius:50%; animation:spin 0.7s linear infinite; }
  .loading-text { font-size:13px; color:var(--text-muted); }
  .error-banner { background:var(--red-dim); border:1px solid rgba(239,68,68,0.3); border-radius:10px; padding:14px 20px; display:flex; align-items:flex-start; gap:12px; margin:20px 28px; font-size:13px; color:var(--red); line-height:1.5; }
  .vehicle-banner { background:linear-gradient(135deg,var(--surface) 0%,var(--surface2) 100%); border-bottom:1px solid var(--border); padding:20px 28px; display:flex; align-items:center; justify-content:space-between; gap:20px; }
  .vehicle-main { display:flex; align-items:center; gap:20px; }
  .vehicle-icon { font-size:42px; }
  .vehicle-year { font-family:'Bebas Neue',sans-serif; font-size:13px; letter-spacing:2px; color:var(--accent); margin-bottom:2px; }
  .vehicle-name { font-family:'Bebas Neue',sans-serif; font-size:30px; letter-spacing:2px; line-height:1; }
  .vehicle-sub { font-size:12px; color:var(--text-muted); margin-top:4px; }
  .vehicle-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:10px; }
  .vb { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:6px; font-size:11px; font-weight:600; }
  .vb-gray  { background:var(--surface2); border:1px solid var(--border); color:var(--text-sub); }
  .vb-red   { background:var(--red-dim); border:1px solid rgba(239,68,68,0.3); color:var(--red); }
  .vb-orange{ background:var(--orange-dim); border:1px solid rgba(249,115,22,0.3); color:var(--orange); }
  .vb-green { background:var(--green-dim); border:1px solid rgba(34,197,94,0.3); color:var(--green); }
  .vehicle-vin { font-family:'JetBrains Mono',monospace; font-size:11px; color:var(--text-muted); margin-top:6px; }
  .content { padding:24px 28px; display:flex; flex-direction:column; gap:20px; }
  .category-strip { display:flex; gap:8px; flex-wrap:wrap; }
  .cat-pill { display:flex; align-items:center; gap:6px; padding:7px 14px; border-radius:20px; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.15s; background:var(--surface); border:1.5px solid var(--border); color:var(--text-sub); }
  .cat-pill:hover { border-color:var(--text-muted); color:var(--text); }
  .cat-pill.active { background:var(--accent-dim); border-color:var(--accent); color:var(--accent); }
  .tier-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
  .tier-card { border-radius:14px; padding:20px; cursor:pointer; transition:all 0.2s; position:relative; border:2px solid var(--border); background:var(--surface); }
  .tier-card:hover { transform:translateY(-2px); }
  .tier-card.selected { transform:translateY(-2px); }
  .tier-check { position:absolute; top:12px; right:12px; width:22px; height:22px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; }
  .tier-rank { font-family:'Bebas Neue',sans-serif; font-size:11px; letter-spacing:2px; margin-bottom:6px; opacity:0.7; }
  .tier-name { font-family:'Bebas Neue',sans-serif; font-size:26px; letter-spacing:2px; margin-bottom:2px; }
  .tier-sub  { font-size:11px; opacity:0.7; margin-bottom:14px; }
  .tier-price { font-family:'Bebas Neue',sans-serif; font-size:38px; letter-spacing:1px; line-height:1; }
  .tier-divider { height:1px; margin:14px 0; opacity:0.2; }
  .tier-feature { font-size:12px; display:flex; align-items:flex-start; gap:6px; margin-bottom:5px; opacity:0.85; }
  .panel { background:var(--surface); border:1px solid var(--border); border-radius:12px; overflow:hidden; }
  .panel-header { padding:14px 20px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; }
  .panel-title { font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); display:flex; align-items:center; gap:8px; }
  .panel-title::before { content:''; display:block; width:3px; height:12px; background:var(--accent); border-radius:2px; }
  .panel-title.red::before { background:var(--red); }
  .panel-title.blue::before { background:var(--blue); }
  .panel-body { padding:16px 20px; }
  .recall-summary { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:16px; }
  .recall-stat { background:var(--surface2); border-radius:8px; padding:12px 14px; }
  .recall-stat-label { font-size:10px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:var(--text-muted); margin-bottom:4px; }
  .recall-stat-value { font-family:'Bebas Neue',sans-serif; font-size:32px; letter-spacing:1px; line-height:1; }
  .recall-item { padding:11px 0; border-bottom:1px solid var(--border); }
  .recall-item:last-child { border-bottom:none; }
  .recall-item-title { font-size:13px; font-weight:600; color:var(--text); margin-bottom:4px; line-height:1.4; }
  .recall-item-meta { font-size:11px; color:var(--text-muted); display:flex; gap:10px; flex-wrap:wrap; align-items:center; }
  .open-badge   { display:inline-flex; align-items:center; gap:3px; padding:2px 8px; border-radius:4px; font-size:10px; font-weight:700; background:var(--red-dim); color:var(--red); border:1px solid rgba(239,68,68,0.3); }
  .closed-badge { display:inline-flex; align-items:center; gap:3px; padding:2px 8px; border-radius:4px; font-size:10px; font-weight:700; background:var(--green-dim); color:var(--green); border:1px solid rgba(34,197,94,0.3); }
  .tsb-item { padding:11px 0; border-bottom:1px solid var(--border); }
  .tsb-item:last-child { border-bottom:none; }
  .tsb-number { font-family:'JetBrains Mono',monospace; font-size:11px; color:var(--accent); margin-bottom:3px; }
  .tsb-title  { font-size:13px; font-weight:600; color:var(--text); margin-bottom:3px; line-height:1.4; }
  .tsb-meta   { font-size:11px; color:var(--text-muted); }
  .maint-locked { display:flex; flex-direction:column; align-items:center; padding:28px; text-align:center; gap:8px; }
  .maint-lock-icon { font-size:28px; opacity:0.4; }
  .maint-item { display:flex; align-items:center; justify-content:space-between; padding:10px 0; border-bottom:1px solid var(--border); }
  .maint-item:last-child { border-bottom:none; }
  .maint-left { display:flex; align-items:center; gap:10px; }
  .maint-icon { font-size:18px; }
  .maint-name { font-size:13px; font-weight:600; }
  .maint-due  { font-size:11px; color:var(--text-muted); margin-top:2px; }
  .maint-status { font-size:11px; font-weight:700; white-space:nowrap; }
  .maint-status.due  { color:var(--red); }
  .maint-status.soon { color:var(--accent); }
  .maint-status.ok   { color:var(--green); }
  .bottom-bar { position:fixed; bottom:0; left:0; right:0; background:rgba(22,27,39,0.97); backdrop-filter:blur(12px); border-top:1px solid var(--border); padding:14px 28px; display:flex; align-items:center; justify-content:space-between; z-index:100; }
  .sel-info { display:flex; align-items:center; gap:12px; }
  .sel-dot  { width:10px; height:10px; border-radius:50%; }
  .sel-label { font-size:13px; color:var(--text-sub); }
  .sel-label strong { color:var(--text); }
  .bar-actions { display:flex; gap:10px; }
  .bar-btn { padding:9px 20px; border-radius:8px; font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; transition:all 0.15s; border:none; }
  .bar-btn-ghost { background:transparent; border:1.5px solid var(--border); color:var(--text-sub); }
  .bar-btn-ghost:hover { border-color:var(--text-sub); color:var(--text); }
  .bar-btn-primary { background:var(--accent); color:#000; }
  .bar-btn-primary:hover { opacity:0.88; }
  .bar-btn-primary:disabled { opacity:0.45; cursor:not-allowed; }
  .pb-80 { padding-bottom:80px; }
  .section-heading { font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); margin-bottom:12px; }
  @keyframes spin { to { transform:rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  .fade-in { animation:fadeIn 0.25s ease; }

  /* TEXT MODAL */
  .text-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.85); z-index:9998; display:flex; align-items:center; justify-content:center; }
  .text-modal { width:min(480px,94vw); background:#161B27; border:1px solid #252D3D; border-radius:16px; overflow:hidden; }
  .text-modal-header { padding:18px 22px; border-bottom:1px solid #252D3D; display:flex; align-items:center; justify-content:space-between; }
  .text-modal-title { font-family:'Bebas Neue',sans-serif; font-size:20px; letter-spacing:2px; color:#F1F5F9; }
  .text-modal-close { background:transparent; border:1px solid #252D3D; border-radius:6px; width:28px; height:28px; color:#94A3B8; cursor:pointer; font-size:14px; }
  .text-modal-close:hover { border-color:#EF4444; color:#EF4444; }
  .text-modal-body { padding:18px 22px; }
  .text-preview { background:#0E1117; border:1px solid #252D3D; border-radius:10px; padding:16px; font-size:13px; color:#94A3B8; line-height:1.7; white-space:pre-wrap; font-family:'DM Sans',sans-serif; margin-bottom:14px; max-height:260px; overflow-y:auto; }
  .text-modal-actions { display:flex; gap:10px; }
  .text-btn { flex:1; padding:10px; border-radius:8px; font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; transition:all 0.15s; border:none; }
  .text-btn-primary { background:#F5A623; color:#000; }
  .text-btn-primary:hover { opacity:0.88; }
  .text-btn-ghost { background:transparent; border:1.5px solid #252D3D; color:#94A3B8; }
  .text-btn-ghost:hover { border-color:#94A3B8; color:#F1F5F9; }
  .text-copied { color:#22C55E; font-size:11px; font-weight:700; text-align:center; margin-top:8px; height:16px; }

  /* PRINT STYLES */
  @media print {
    .screen-only { display:none !important; }
    .print-estimate { display:block !important; padding:40px; background:#fff; color:#000; font-family:'DM Sans',sans-serif; }
    .print-store { font-size:11px; color:#666; margin-bottom:4px; }
    .print-title { font-size:26px; font-weight:800; letter-spacing:2px; margin-bottom:20px; }
    .print-vehicle { font-size:16px; font-weight:700; margin-bottom:4px; }
    .print-vin { font-size:11px; color:#666; font-family:monospace; margin-bottom:24px; }
    .print-section-label { font-size:10px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:#888; margin-bottom:10px; border-bottom:1px solid #eee; padding-bottom:4px; }
    .print-tier-row { display:flex; justify-content:space-between; align-items:center; padding:10px 0; border-bottom:1px solid #f0f0f0; }
    .print-tier-name { font-size:14px; font-weight:700; }
    .print-tier-detail { font-size:12px; color:#555; margin-top:2px; }
    .print-tier-price { font-size:22px; font-weight:800; }
    .print-tier-best .print-tier-name { color:#B8800A; }
    .print-tier-best .print-tier-price { color:#B8800A; }
    .print-tier-better .print-tier-name { color:#1D4ED8; }
    .print-tier-better .print-tier-price { color:#1D4ED8; }
    .print-tier-good .print-tier-name { color:#15803D; }
    .print-tier-good .print-tier-price { color:#15803D; }
    .print-selected { background:#fffbeb; border-radius:6px; padding:2px 8px; font-size:10px; font-weight:700; color:#B8800A; margin-left:8px; }
    .print-footer { margin-top:28px; font-size:11px; color:#999; }
  }
`;

function getMaintenanceItems(mileage) {
  const mi = parseInt(mileage, 10) || 0;
  return [
    { id:"oil",   icon:"🛢️", name:"Oil Change",           interval:5000  },
    { id:"tire",  icon:"⚫", name:"Tire Rotation",         interval:7500  },
    { id:"cabin", icon:"🌬️", name:"Cabin Air Filter",      interval:15000 },
    { id:"air",   icon:"🔧", name:"Engine Air Filter",     interval:20000 },
    { id:"brake", icon:"💧", name:"Brake Fluid",           interval:30000 },
    { id:"spark", icon:"⚡", name:"Spark Plugs",           interval:30000 },
    { id:"cool",  icon:"🌡️", name:"Coolant Flush",         interval:50000 },
    { id:"trans", icon:"⚙️", name:"Transmission Service",  interval:60000 },
  ].map(item => {
    const last    = Math.floor(mi / item.interval) * item.interval;
    const nextDue = last + item.interval;
    const remaining = nextDue - mi;
    return { ...item, nextDue, remaining, status: remaining <= 0 ? "due" : remaining <= 1000 ? "soon" : "ok" };
  });
}

export default function AdvisorView({ currentUser, onLogout, onNavigate }) {
  const [vinInput,      setVinInput]      = useState("");
  const [plateInput,    setPlateInput]    = useState("");
  const [selectedState, setSelectedState] = useState("OH");
  const [vehicle,       setVehicle]       = useState(null);
  const [recalls,       setRecalls]       = useState(null);
  const [tsbs,          setTsbs]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [activeCategory,setActiveCategory]= useState("oil");
  const [selectedTier,  setSelectedTier]  = useState(null);
  const [mileage,       setMileage]       = useState("");
  const [mileageSaved,  setMileageSaved]  = useState(false);
  const [vinHistory,    setVinHistory]    = useState(null);
  const [showScanner,   setShowScanner]   = useState(false);
  const [partsData,     setPartsData]     = useState(null);
  const [partsLoading,  setPartsLoading]  = useState(false);
  const [defaultState,  setDefaultState]  = useState("OH");
  const [showTextModal, setShowTextModal] = useState(false);
  const [textCopied,    setTextCopied]    = useState(false);
  const vinRef = useRef(null);

  // ── Parse API response ──────────────────────────────────
  const parseVehicleData = (data, resolvedVin) => {
    const v = data.vehicle || data;
    return {
      vin:        resolvedVin || v.vin || "",
      year:       v.year || v.modelYear || "—",
      make:       v.make || "—",
      model:      v.model || "—",
      trim:       v.trim || v.trimLevel || "",
      engine:     v.engine || "",
      drive:      v.drive || v.driveType || "",
      body:       v.body || v.bodyStyle || "",
      color:      v.exteriorColor || v.color || "",
    };
  };

  const parseRecalls = (data) => {
    if (!data) return { total: 0, open: 0, items: [] };
    // Cloud Function returns pre-processed NHTSA data with total/open/items
    if (typeof data.total === "number") return data;
    // Fallback for raw arrays
    const arr = Array.isArray(data) ? data : [];
    return { total: arr.length, open: arr.length, items: arr };
  };

  const parseTsbs = (data) => {
    if (!data) return [];
    const list = Array.isArray(data) ? data
      : data.tsbs || data.results || data.data || [];
    return Array.isArray(list) ? list : [];
  };

  // ── VIN scanner callback ─────────────────────────────────
  const handleScanDetected = useCallback((scannedVin) => {
    setShowScanner(false);
    setVinInput(scannedVin);
    handleLookup("vin", scannedVin);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Main lookup (type: "vin"|"plate", vinOverride for scanner) ──
  const handleLookup = async (type, vinOverride) => {
    setError(""); setVehicle(null); setRecalls(null); setTsbs(null);
    setSelectedTier(null); setMileage(""); setMileageSaved(false); setVinHistory(null);

    const val = vinOverride
      ? vinOverride.trim().toUpperCase()
      : type === "vin"
        ? vinInput.trim().toUpperCase()
        : plateInput.trim().toUpperCase();

    if (!val) {
      setError(type === "vin" ? "Please enter a VIN number." : "Please enter a license plate.");
      return;
    }
    if (type === "vin" && val.length !== 17) {
      setError("VIN must be exactly 17 characters.");
      return;
    }

    setLoading(true);
    try {
      const url = type === "vin"
        ? `${FUNCTIONS_BASE}/vinLookup?vin=${encodeURIComponent(val)}`
        : `${FUNCTIONS_BASE}/plateLookup?plate=${encodeURIComponent(val)}&state=${selectedState}`;

      const res = await fetch(url);
      const json = await res.json();

      if (!res.ok) {
        setError(json.error || `Lookup failed (${res.status}). Please try again.`);
        setLoading(false);
        return;
      }

      const resolvedVin = json.resolvedVin || val;
      setVehicle(parseVehicleData(json, resolvedVin));
      setRecalls(parseRecalls(json.recalls));
      setTsbs(parseTsbs(json.tsbs));
      loadVinHistory(resolvedVin);

    } catch (e) {
      console.error("Lookup error:", e);
      setError("Connection error. Make sure the app is deployed and try again.");
    }
    setLoading(false);
  };

  const loadVinHistory = async (vin) => {
    if (!currentUser?.storeId || !vin) return;
    try {
      const snap = await getDoc(doc(db, "stores", currentUser.storeId, "vehicles", vin));
      if (snap.exists()) setVinHistory(snap.data());
    } catch (e) { /* silent */ }
  };

  // Called whenever vehicle or activeCategory changes
  const loadPartsData = async (category, make, model, year) => {
    if (!make || !category) return;
    setPartsLoading(true);
    setPartsData(null);
    try {
      const params = new URLSearchParams({ category, make, model: model || "", year: year || "" });
      const res = await fetch(`${FUNCTIONS_BASE}/partsLookup?${params}`);
      const json = await res.json();
      if (res.ok) setPartsData(json);
      else console.warn("partsLookup error:", json.error);
    } catch (e) {
      console.error("partsLookup fetch error:", e);
    }
    setPartsLoading(false);
  };

  const saveMileage = async () => {
    if (!mileage || !vehicle?.vin || !currentUser?.storeId) return;
    try {
      await setDoc(
        doc(db, "stores", currentUser.storeId, "vehicles", vehicle.vin),
        {
          vin: vehicle.vin, make: vehicle.make, model: vehicle.model, year: vehicle.year,
          lastMileage: parseInt(mileage, 10),
          lastMileageAt: serverTimestamp(),
          lastAdvisor: currentUser.name,
          lastAdvisorId: currentUser.uid,
        },
        { merge: true }
      );
      setMileageSaved(true);
    } catch (e) { console.error("Mileage save:", e); }
  };

  const clearLookup = () => {
    setVinInput(""); setPlateInput(""); setVehicle(null);
    setRecalls(null); setTsbs(null); setError("");
    setSelectedTier(null); setMileage(""); setMileageSaved(false);
    setVinHistory(null); setPartsData(null);
    setTimeout(() => vinRef.current?.focus(), 50);
  };

  const maintenanceItems = mileage ? getMaintenanceItems(mileage) : [];

  // Load store default state on mount
  useEffect(() => {
    const loadStoreDefaults = async () => {
      if (!currentUser?.storeId) return;
      try {
        const snap = await getDoc(doc(db, "stores", currentUser.storeId));
        if (snap.exists() && snap.data().defaultState) {
          setSelectedState(snap.data().defaultState);
          setDefaultState(snap.data().defaultState);
        }
      } catch (e) { /* silent */ }
    };
    loadStoreDefaults();
  }, [currentUser?.storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch parts pricing whenever a vehicle is loaded or category tab changes
  useEffect(() => {
    if (vehicle?.make) {
      loadPartsData(activeCategory, vehicle.make, vehicle.model, vehicle.year);
    }
  }, [vehicle, activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Log a lookup to Firestore when advisor selects a tier
  // Stored at: stores/{storeId}/lookups/{auto-id}
  const logLookup = async (tier) => {
    if (!currentUser?.storeId || !vehicle) return;
    try {
      await addDoc(collection(db, "stores", currentUser.storeId, "lookups"), {
        vin:          vehicle.vin,
        year:         vehicle.year,
        make:         vehicle.make,
        model:        vehicle.model,
        trim:         vehicle.trim || "",
        category:     activeCategory,
        tierSelected: tier,
        price:        partsData?.[tier]?.price || null,
        brand:        partsData?.[tier]?.brand || null,
        partName:     partsData?.[tier]?.partName || null,
        advisorId:    currentUser.uid,
        advisorName:  currentUser.name,
        storeId:      currentUser.storeId,
        timestamp:    serverTimestamp(),
        source:       partsData?.source || "estimated",
      });
    } catch (e) { console.error("logLookup error:", e); }
  };

  // ── Print estimate ───────────────────────────────────────
  const handlePrint = () => window.print();

  // ── Build SMS message ────────────────────────────────────
  const buildTextMessage = () => {
    if (!vehicle || !partsData) return "";
    const cat = CATEGORIES.find(c => c.id === activeCategory)?.label || activeCategory;
    const lines = [
      `Hi! Here's your ${cat} estimate for your ${vehicle.year} ${vehicle.make} ${vehicle.model}:`,
      "",
      `⭐ BEST (OEM): $${partsData.best?.price?.toFixed(2)} — ${partsData.best?.partName}`,
      `   Warranty: ${partsData.best?.warranty}`,
      "",
      `🔵 BETTER: $${partsData.better?.price?.toFixed(2)} — ${partsData.better?.partName}`,
      `   Warranty: ${partsData.better?.warranty}`,
      "",
      `🟢 GOOD: $${partsData.good?.price?.toFixed(2)} — ${partsData.good?.partName}`,
      `   Warranty: ${partsData.good?.warranty}`,
    ];
    if (selectedTier && partsData[selectedTier]) {
      lines.push("", `✓ Your advisor recommended the ${selectedTier.toUpperCase()} option at $${partsData[selectedTier]?.price?.toFixed(2)}.`);
    }
    lines.push("", "Questions? Reply to this message or call the service desk.");
    return lines.join("\n");
  };

  const handleTextCustomer = () => {
    const msg = buildTextMessage();
    if (!msg) return;
    const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = `sms:?body=${encodeURIComponent(msg)}`;
    } else {
      setShowTextModal(true);
      setTextCopied(false);
    }
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(buildTextMessage());
      setTextCopied(true);
      setTimeout(() => setTextCopied(false), 2500);
    } catch (e) { /* silent */ }
  };

  return (
    <>
      <style>{styles}</style>

      {/* SCREEN-ONLY CONTENT — hidden during print */}
      <div className="screen-only">

      {/* HEADER */}
      <header className="hdr">
        <div className="hdr-logo">
          <div className="hdr-logo-icon">L</div>
          <div className="hdr-logo-text">LANE<span>IQ</span></div>
        </div>
        <span className="hdr-badge">👨‍💼 Advisor View</span>
        <div className="hdr-right">
          <div className="hdr-user">Logged in as <strong>{currentUser?.name || "Advisor"}</strong></div>
          {(currentUser?.role === "admin" || currentUser?.role === "service_manager" || currentUser?.role === "parts_manager") && (
            <button className="hdr-btn" onClick={() => onNavigate?.(
              currentUser?.role === "parts_manager" ? "parts" : "admin"
            )}>← Dashboard</button>
          )}
          <button className="hdr-btn" onClick={onLogout}>Sign Out</button>
        </div>
      </header>

      {/* SEARCH ZONE */}
      <div className="search-zone">
        <div className="search-row">
          {/* VIN */}
          <div className="search-group">
            <div className="search-label">VIN Lookup</div>
            <div style={{display:"flex", gap:8, alignItems:"center"}}>
              <input ref={vinRef} className="search-input" placeholder="17-character VIN"
                value={vinInput}
                onChange={e => setVinInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && handleLookup("vin")}
                maxLength={17} style={{letterSpacing:2, flex:1}} />
              <button
                title="Scan VIN barcode with camera"
                onClick={() => setShowScanner(true)}
                style={{
                  flexShrink:0, height:42, width:46,
                  background:"#161B27", border:"1px solid #F5A623",
                  borderRadius:8, cursor:"pointer", fontSize:20,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  transition:"all 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background="#F5A62322"}
                onMouseLeave={e => e.currentTarget.style.background="#161B27"}
              >📷</button>
            </div>
          </div>

          <div className="or-divider" style={{paddingBottom:0,alignSelf:"center",marginTop:16,paddingTop:4}}>
            <span style={{padding:"0 10px",color:"var(--text-muted)",fontSize:11,fontWeight:700}}>OR</span>
          </div>

          {/* PLATE */}
          <div className="search-group">
            <div className="search-label">License Plate</div>
            <div className="plate-row">
              <select className="state-select" value={selectedState}
                onChange={e => setSelectedState(e.target.value)}>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>{code} — {name}</option>
                ))}
              </select>
              <input className="search-input" placeholder="ABC 1234"
                value={plateInput}
                onChange={e => setPlateInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && handleLookup("plate")}
                style={{width:130}} />
            </div>
            <div className="state-default-note">
              Defaults to {US_STATES.find(([c])=>c===defaultState)?.[1]||"Ohio"} · <em>Change state for out-of-state vehicles</em>
            </div>
          </div>

          {/* BUTTONS */}
          <div style={{display:"flex",gap:8,alignSelf:"flex-end",paddingBottom:0}}>
            <button className="search-btn"
              onClick={() => vinInput ? handleLookup("vin") : handleLookup("plate")}
              disabled={loading}>
              {loading ? "Searching..." : "🔍 Search"}
            </button>
            {vehicle && <button className="clear-btn" onClick={clearLookup}>✕ Clear</button>}
          </div>
        </div>
      </div>

      {/* ERROR */}
      {error && <div className="error-banner">⚠ {error}</div>}

      {/* LOADING */}
      {loading && (
        <div className="loading-wrap">
          <div className="loading-spinner" />
          <div className="loading-text">Looking up vehicle data...</div>
        </div>
      )}

      {/* EMPTY */}
      {!loading && !vehicle && !error && (
        <div className="empty-state">
          <div className="empty-icon">🚗</div>
          <div className="empty-title">Ready for Lookup</div>
          <div className="empty-sub">Enter a VIN or license plate above to pull up vehicle details, open recalls, TSBs, and Good / Better / Best pricing.</div>
        </div>
      )}

      {/* RESULTS */}
      {!loading && vehicle && (
        <div className="fade-in pb-80">

          {/* VEHICLE BANNER */}
          <div className="vehicle-banner">
            <div className="vehicle-main">
              <div className="vehicle-icon">🚗</div>
              <div>
                <div className="vehicle-year">{vehicle.year}</div>
                <div className="vehicle-name">{vehicle.make} {vehicle.model}</div>
                <div className="vehicle-sub">
                  {[vehicle.trim, vehicle.engine, vehicle.drive].filter(Boolean).join(" · ")}
                  {vehicle.color ? ` · ${vehicle.color}` : ""}
                </div>
                <div className="vehicle-badges">
                  {vehicle.body && <span className="vb vb-gray">{vehicle.body}</span>}
                  {recalls?.open > 0 && (
                    <span className="vb vb-red">🔴 {recalls.open} Open Recall{recalls.open !== 1 ? "s" : ""}</span>
                  )}
                  {recalls?.open === 0 && recalls?.total >= 0 && (
                    <span className="vb vb-green">✓ No Open Recalls</span>
                  )}
                  {tsbs?.length > 0 && (
                    <span className="vb vb-orange">⚠ {tsbs.length} TSB{tsbs.length !== 1 ? "s" : ""}</span>
                  )}
                </div>
                <div className="vehicle-vin">VIN: {vehicle.vin}</div>
              </div>
            </div>
            {vinHistory?.lastMileage && (
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"var(--text-muted)",marginBottom:4}}>Last Visit</div>
                <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:15,color:"var(--text-sub)"}}>
                  {vinHistory.lastMileage.toLocaleString()} mi
                </div>
                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>by {vinHistory.lastAdvisor}</div>
              </div>
            )}
          </div>

          {/* MILEAGE BAR */}
          <div className="mileage-bar">
            <span className="mileage-label">📍 Odometer:</span>
            <input className="mileage-input" type="number" placeholder="Enter current mileage"
              value={mileage}
              onChange={e => { setMileage(e.target.value); setMileageSaved(false); }}
              onKeyDown={e => e.key === "Enter" && saveMileage()} />
            {mileage && !mileageSaved && (
              <button className="mileage-save-btn" onClick={saveMileage}>Save to Vehicle History</button>
            )}
            {mileageSaved && <span style={{fontSize:12,color:"var(--green)",fontWeight:700}}>✓ Saved</span>}
            {!mileage && <span className="mileage-required">⚠ Enter mileage to unlock maintenance schedule</span>}
            {vinHistory?.lastMileage && !mileage && (
              <span className="mileage-history">Last recorded: <strong>{vinHistory.lastMileage.toLocaleString()} mi</strong></span>
            )}
          </div>

          <div className="content">

            {/* CATEGORIES */}
            <div>
              <div className="section-heading">Service Category</div>
              <div className="category-strip">
                {CATEGORIES.map(c => (
                  <div key={c.id}
                    className={`cat-pill ${activeCategory === c.id ? "active" : ""}`}
                    onClick={() => { setActiveCategory(c.id); setSelectedTier(null); }}>
                    {c.icon} {c.label}
                  </div>
                ))}
              </div>
            </div>

            {/* TIER CARDS */}
            <div>
              <div className="section-heading" style={{display:"flex",alignItems:"center",gap:10}}>
                <span>Good / Better / Best — {CATEGORIES.find(c=>c.id===activeCategory)?.label}</span>
                {partsData?.source === "estimated" && (
                  <span style={{
                    fontSize:10,fontWeight:700,letterSpacing:1,
                    color:"#F5A623",border:"1px solid rgba(245,166,35,0.4)",
                    borderRadius:4,padding:"2px 7px",fontFamily:"'DM Sans',sans-serif",
                  }}>EST. MARKET RANGE</span>
                )}
                {partsData?.source === "partstech" && (
                  <span style={{
                    fontSize:10,fontWeight:700,letterSpacing:1,
                    color:"#22C55E",border:"1px solid rgba(34,197,94,0.4)",
                    borderRadius:4,padding:"2px 7px",fontFamily:"'DM Sans',sans-serif",
                  }}>LIVE PRICING</span>
                )}
                {partsLoading && (
                  <span style={{fontSize:11,color:"var(--text-muted)",fontFamily:"'DM Sans',sans-serif"}}>
                    Loading pricing...
                  </span>
                )}
              </div>
              <div className="tier-grid">
                {["best","better","good"].map(tier => {
                  const cfg  = TIER_CONFIG[tier];
                  const sel  = selectedTier === tier;
                  const data = partsData?.[tier];  // { partName, brand, partNumber, price, warranty, description }
                  return (
                    <div key={tier} className={`tier-card ${sel ? "selected" : ""}`}
                      style={{
                        background: sel ? cfg.dim : "var(--surface)",
                        borderColor: sel ? cfg.color : "var(--border)",
                        opacity: partsLoading ? 0.5 : 1,
                        transition: "opacity 0.2s",
                      }}
                      onClick={() => { if (!partsLoading) { setSelectedTier(tier); logLookup(tier); } }}>
                      {sel && (
                        <div className="tier-check" style={{background:cfg.color,color:"#000"}}>✓</div>
                      )}
                      <div className="tier-rank" style={{color:cfg.color}}>{tier.toUpperCase()}</div>
                      <div className="tier-name" style={{color:cfg.color}}>{cfg.label}</div>
                      <div className="tier-sub" style={{color:cfg.color}}>
                        {data ? data.brand : cfg.sub}
                      </div>

                      {/* PRICE */}
                      {data ? (
                        <div className="tier-price" style={{color:sel?cfg.color:"var(--text)"}}>
                          ${data.price.toFixed(2)}
                        </div>
                      ) : (
                        <div className="tier-price">
                          <span style={{fontSize:13,color:"var(--text-muted)",fontFamily:"'DM Sans',sans-serif",fontWeight:400}}>
                            {partsLoading ? "Fetching..." : "Select a vehicle"}
                          </span>
                        </div>
                      )}

                      <div className="tier-divider" style={{background:cfg.color}} />

                      {/* PART NAME */}
                      {data && (
                        <div className="tier-feature" style={{color:sel?cfg.color:"var(--text-sub)",fontWeight:600}}>
                          🔧 {data.partName}
                        </div>
                      )}

                      {/* DESCRIPTION */}
                      {data && (
                        <div className="tier-feature" style={{color:sel?cfg.color:"var(--text-sub)"}}>
                          {data.description}
                        </div>
                      )}

                      {/* WARRANTY */}
                      <div className="tier-feature" style={{color:sel?cfg.color:"var(--text-sub)"}}>
                        🛡️ {data ? data.warranty : (tier==="best"?"OEM Warranty":tier==="better"?"2 Year / Unlimited Miles":"1 Year Limited")}
                      </div>

                      {/* PART NUMBER — subtle, JetBrains Mono */}
                      {data?.partNumber && data.partNumber !== "—" && (
                        <div style={{
                          marginTop:8,fontSize:10,
                          fontFamily:"'JetBrains Mono',monospace",
                          color:"var(--text-muted)",letterSpacing:0.5,
                        }}>
                          #{data.partNumber}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RECALL + TSB PANELS */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>

              {/* RECALLS */}
              <div className="panel">
                <div className="panel-header">
                  <div className="panel-title red">🔴 Recall History</div>
                </div>
                <div className="panel-body">
                  <div className="recall-summary">
                    <div className="recall-stat">
                      <div className="recall-stat-label">Total Recalls</div>
                      <div className="recall-stat-value" style={{color:recalls?.total>0?"var(--accent)":"var(--green)"}}>
                        {recalls?.total ?? "—"}
                      </div>
                    </div>
                    <div className="recall-stat">
                      <div className="recall-stat-label">Open / Unrepaired</div>
                      <div className="recall-stat-value" style={{color:recalls?.open>0?"var(--red)":"var(--green)"}}>
                        {recalls?.open ?? "—"}
                      </div>
                    </div>
                  </div>
                  {recalls?.items?.length === 0 ? (
                    <div style={{fontSize:12,color:"var(--green)",display:"flex",alignItems:"center",gap:6}}>
                      ✓ No recall history found for this VIN
                    </div>
                  ) : (
                    recalls?.items?.slice(0, 5).map((r, i) => (
                      <div key={i} className="recall-item">
                        {r.parkIt && (
                          <div style={{background:"var(--red-dim)",border:"1px solid var(--red)",borderRadius:6,padding:"4px 10px",marginBottom:6,fontSize:11,fontWeight:700,color:"var(--red)"}}>
                            🚫 DO NOT DRIVE — Immediate safety risk
                          </div>
                        )}
                        {r.parkOutside && !r.parkIt && (
                          <div style={{background:"var(--orange-dim)",border:"1px solid var(--orange)",borderRadius:6,padding:"4px 10px",marginBottom:6,fontSize:11,fontWeight:700,color:"var(--orange)"}}>
                            🔥 PARK OUTSIDE — Fire/explosion risk
                          </div>
                        )}
                        <div className="recall-item-title">
                          {r.summary || r.consequence || r.component || "Open Recall"}
                        </div>
                        <div className="recall-item-meta">
                          {r.campaignNumber && (
                            <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{r.campaignNumber}</span>
                          )}
                          {r.component && <span>{r.component}</span>}
                          {r.reportDate && <span>{r.reportDate.substring(0,4)}</span>}
                          {r.overTheAir
                            ? <span style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:700,background:"var(--blue-dim)",color:"var(--blue)",border:"1px solid rgba(59,130,246,0.3)"}}>OTA Update</span>
                            : <span className="open-badge">⚠ Dealer Visit Required</span>
                          }
                        </div>
                        {r.remedy && (
                          <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4,lineHeight:1.4}}>
                            <strong style={{color:"var(--text-sub)"}}>Remedy:</strong> {r.remedy.substring(0,120)}{r.remedy.length > 120 ? "..." : ""}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  {recalls?.items?.length > 5 && (
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:8,paddingTop:8,borderTop:"1px solid var(--border)"}}>
                      +{recalls.items.length - 5} additional recalls on record
                    </div>
                  )}
                </div>
              </div>

              {/* TSBs */}
              <div className="panel">
                <div className="panel-header">
                  <div className="panel-title blue">⚠ Technical Service Bulletins</div>
                  {tsbs?.length > 0 && (
                    <span style={{fontSize:11,color:"var(--text-muted)"}}>{tsbs.length} found</span>
                  )}
                </div>
                <div className="panel-body">
                  {!tsbs || tsbs.length === 0 ? (
                    <div style={{fontSize:12,color:"var(--text-muted)"}}>No TSBs found for this vehicle</div>
                  ) : (
                    tsbs.slice(0, 6).map((t, i) => (
                      <div key={i} className="tsb-item">
                        <div className="tsb-number">
                          {t.bulletinNumber || t.tsbNumber || t.id || `TSB-${i+1}`}
                        </div>
                        <div className="tsb-title">
                          {t.summary || t.subject || t.title || t.description || "Service Bulletin"}
                        </div>
                        {(t.component || t.date) && (
                          <div className="tsb-meta">
                            {t.component && <span>{t.component}</span>}
                            {t.component && t.date && " · "}
                            {t.date && <span>{new Date(t.date).toLocaleDateString()}</span>}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  {tsbs?.length > 6 && (
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:8,paddingTop:8,borderTop:"1px solid var(--border)"}}>
                      +{tsbs.length - 6} more bulletins
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* MAINTENANCE SCHEDULE */}
            <div className="panel">
              <div className="panel-header">
                <div className="panel-title">🔧 Maintenance Schedule</div>
                {mileage && (
                  <span style={{fontSize:11,color:"var(--text-muted)"}}>
                    Based on {parseInt(mileage).toLocaleString()} mi
                  </span>
                )}
              </div>
              <div className="panel-body">
                {!mileage ? (
                  <div className="maint-locked">
                    <div className="maint-lock-icon">🔒</div>
                    <div style={{fontSize:12,color:"var(--text-muted)"}}>
                      Enter the odometer reading above to unlock the maintenance schedule
                    </div>
                  </div>
                ) : (
                  maintenanceItems.map(item => (
                    <div key={item.id} className="maint-item">
                      <div className="maint-left">
                        <div className="maint-icon">{item.icon}</div>
                        <div>
                          <div className="maint-name">{item.name}</div>
                          <div className="maint-due">
                            Every {item.interval.toLocaleString()} mi · Next due {item.nextDue.toLocaleString()} mi
                          </div>
                        </div>
                      </div>
                      <div className={`maint-status ${item.status}`}>
                        {item.status === "due"  && "⚠ Overdue"}
                        {item.status === "soon" && `⚡ Due in ${item.remaining.toLocaleString()} mi`}
                        {item.status === "ok"   && `✓ ${item.remaining.toLocaleString()} mi remaining`}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* BOTTOM BAR */}
      {vehicle && (
        <div className="bottom-bar">
          <div className="sel-info">
            <div className="sel-dot" style={{background: selectedTier ? TIER_CONFIG[selectedTier].color : "var(--text-muted)"}} />
            <div className="sel-label">
              {selectedTier
                ? <><strong>{TIER_CONFIG[selectedTier].label}</strong> tier selected — {CATEGORIES.find(c=>c.id===activeCategory)?.label}</>
                : "Select a tier to build estimate"}
            </div>
          </div>
          <div className="bar-actions">
            <button className="bar-btn bar-btn-ghost" onClick={handlePrint}>🖨️ Print Estimate</button>
            <button className="bar-btn bar-btn-primary" disabled={!selectedTier || !partsData}
              onClick={handleTextCustomer}>
              📱 Text to Customer
            </button>
          </div>
        </div>
      )}

      {/* VIN SCANNER MODAL */}
      {showScanner && (
        <VinScanner
          onDetected={handleScanDetected}
          onClose={() => setShowScanner(false)}
        />
      )}

      </div>{/* end screen-only */}

      {/* PRINT ESTIMATE — hidden on screen, visible only when printing */}
      {vehicle && partsData && (
        <div className="print-estimate" style={{display:"none"}}>
          <div className="print-store">{currentUser?.storeName || "LaneIQ Estimate"}</div>
          <div className="print-title">SERVICE ESTIMATE</div>
          <div className="print-vehicle">{vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}</div>
          <div className="print-vin">VIN: {vehicle.vin}</div>
          <div className="print-section-label">
            {CATEGORIES.find(c=>c.id===activeCategory)?.label} — Good / Better / Best Options
          </div>
          {["best","better","good"].map(tier => {
            const d = partsData[tier];
            const cfg = TIER_CONFIG[tier];
            if (!d) return null;
            return (
              <div key={tier} className={`print-tier-row print-tier-${tier}`}>
                <div>
                  <div className="print-tier-name">
                    {cfg.label}
                    {selectedTier === tier && <span className="print-selected">✓ SELECTED</span>}
                  </div>
                  <div className="print-tier-detail">{d.partName} · {d.brand}</div>
                  <div className="print-tier-detail">Warranty: {d.warranty}</div>
                </div>
                <div className="print-tier-price">${d.price?.toFixed(2)}</div>
              </div>
            );
          })}
          <div className="print-footer">
            Printed from LaneIQ · {new Date().toLocaleDateString()} · Prices are estimates and subject to change.
          </div>
        </div>
      )}

      {/* TEXT TO CUSTOMER MODAL (desktop fallback) */}
      {showTextModal && (
        <div className="text-overlay" onClick={() => setShowTextModal(false)}>
          <div className="text-modal" onClick={e => e.stopPropagation()}>
            <div className="text-modal-header">
              <div className="text-modal-title">📱 Text to Customer</div>
              <button className="text-modal-close" onClick={() => setShowTextModal(false)}>✕</button>
            </div>
            <div className="text-modal-body">
              <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:10}}>
                On a tablet or phone this opens your SMS app automatically.
                On desktop, copy this message and paste it into your messaging tool.
              </div>
              <div className="text-preview">{buildTextMessage()}</div>
              <div className="text-modal-actions">
                <button className="text-btn text-btn-ghost" onClick={() => setShowTextModal(false)}>Close</button>
                <button className="text-btn text-btn-primary" onClick={handleCopyText}>
                  {textCopied ? "✓ Copied!" : "Copy Message"}
                </button>
              </div>
              {textCopied && <div className="text-copied">✓ Copied to clipboard</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
